import { useState, useCallback } from "react";
import { Search, Send, ChevronLeft, ChevronRight } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { RosterRowStatusBadge } from "./RosterRowStatusBadge";
import type { RosterEntry, RosterEnrollmentStatus } from "../types";

interface RosterTableProps {
  items: RosterEntry[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  page: number;
  pageSize: number;
  search: string;
  statusFilter: RosterEnrollmentStatus | null;
  onSearchChange: (search: string) => void;
  onStatusFilterChange: (status: RosterEnrollmentStatus | null) => void;
  onPageChange: (page: number) => void;
  onSendInvites: (emails: string[]) => Promise<void> | void;
  isSendingInvites: boolean;
}

const STATUS_OPTIONS: Array<{
  value: RosterEnrollmentStatus | null;
  labelKey: string;
}> = [
  { value: null, labelKey: "roster.statusAll" },
  { value: "ACTIVE", labelKey: "roster.statusActive" },
  { value: "PENDING", labelKey: "roster.statusPending" },
  { value: "NOT_INVITED", labelKey: "roster.statusNotInvited" },
];

export function RosterTable({
  items,
  total,
  hasMore,
  isLoading,
  page,
  pageSize,
  search,
  statusFilter,
  onSearchChange,
  onStatusFilterChange,
  onPageChange,
  onSendInvites,
  isSendingInvites,
}: RosterTableProps) {
  const { t } = useLocale();
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());

  const toggleEmail = useCallback((email: string) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    const invitableEmails = items
      .filter((item) => item.enrollmentStatus === "NOT_INVITED")
      .map((item) => item.workEmail);

    setSelectedEmails((prev) => {
      const allSelected = invitableEmails.every((e) => prev.has(e));
      if (allSelected) {
        const next = new Set(prev);
        for (const e of invitableEmails) next.delete(e);
        return next;
      }
      return new Set([...prev, ...invitableEmails]);
    });
  }, [items]);

  const handleSendInvites = useCallback(async () => {
    if (selectedEmails.size > 0) {
      await onSendInvites(Array.from(selectedEmails));
      setSelectedEmails(new Set());
    }
  }, [selectedEmails, onSendInvites]);

  const invitableOnPage = items.filter(
    (i) => i.enrollmentStatus === "NOT_INVITED"
  );
  const allInvitableSelected =
    invitableOnPage.length > 0 &&
    invitableOnPage.every((i) => selectedEmails.has(i.workEmail));

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, total);

  return (
    <div className="rounded-lg border border-border bg-card">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        {/* Search */}
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t("roster.searchPlaceholder")}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full rounded-md border border-input bg-background py-1.5 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-1">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.labelKey}
              type="button"
              onClick={() => onStatusFilterChange(opt.value)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                statusFilter === opt.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {t(opt.labelKey)}
            </button>
          ))}
        </div>

        {/* Send Invites Button */}
        {selectedEmails.size > 0 && (
          <button
            type="button"
            onClick={handleSendInvites}
            disabled={isSendingInvites}
            className="flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {isSendingInvites ? (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            {t("roster.sendInvites", { count: String(selectedEmails.size) })}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <th className="w-10 px-4 py-2">
                <input
                  type="checkbox"
                  checked={allInvitableSelected && invitableOnPage.length > 0}
                  onChange={toggleAll}
                  className="rounded border-input"
                  title={t("roster.selectAllInvitable")}
                />
              </th>
              <th className="px-3 py-2 font-medium text-muted-foreground">
                {t("roster.colName")}
              </th>
              <th className="px-3 py-2 font-medium text-muted-foreground">
                {t("roster.colEmail")}
              </th>
              <th className="px-3 py-2 font-medium text-muted-foreground">
                {t("roster.colDepartment")}
              </th>
              <th className="px-3 py-2 font-medium text-muted-foreground">
                {t("roster.colJobTitle")}
              </th>
              <th className="px-3 py-2 font-medium text-muted-foreground">
                {t("roster.colStatus")}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && items.length === 0
              ? Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-border">
                    <td colSpan={6} className="px-4 py-3">
                      <div className="h-5 animate-pulse rounded bg-muted" />
                    </td>
                  </tr>
                ))
              : items.map((entry) => {
                  const isInvitable =
                    entry.enrollmentStatus === "NOT_INVITED";
                  return (
                    <tr
                      key={entry._id}
                      className="border-b border-border transition-colors hover:bg-muted/30"
                    >
                      <td className="px-4 py-2">
                        {isInvitable ? (
                          <input
                            type="checkbox"
                            checked={selectedEmails.has(entry.workEmail)}
                            onChange={() => toggleEmail(entry.workEmail)}
                            className="rounded border-input"
                          />
                        ) : (
                          <span className="block h-4 w-4" />
                        )}
                      </td>
                      <td className="px-3 py-2 font-medium text-foreground">
                        {entry.firstName || entry.lastName
                          ? `${entry.firstName ?? ""} ${entry.lastName ?? ""}`.trim()
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {entry.workEmail}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {entry.department ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {entry.jobTitle ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        <RosterRowStatusBadge
                          status={entry.enrollmentStatus}
                        />
                      </td>
                    </tr>
                  );
                })}
            {!isLoading && items.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                >
                  {t("roster.emptyState")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
          <span>
            {t("common.showingRange", { start: startRow, end: endRow, total })}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
              className="rounded p-1 transition-colors hover:bg-muted disabled:opacity-30"
              aria-label={t("common.previousPage")}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: Math.max(1, Math.ceil(total / pageSize)) }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onPageChange(i)}
                className={`flex h-7 min-w-[28px] items-center justify-center rounded px-1.5 text-xs font-medium transition-colors ${
                  i === page
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={!hasMore}
              className="rounded p-1 transition-colors hover:bg-muted disabled:opacity-30"
              aria-label={t("common.nextPage")}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
